import { useEffect, useState } from 'react';
import { ArrowLeft, RefreshCw, Info, TrendingUp, MessageCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/contexts/AuthContext';

/**
 * User-facing Scoring v2 detail page. Shows the current user's Meet Score
 * and Responsiveness Score, each with its 5-category breakdown expressed as
 * progress bars (points / weight). Owner-only — never fetch another user's
 * breakdown here.
 *
 * Backend: GET /v2/scores/me · POST /v2/scores/recalculate/me
 */
export default function ScoringDetail() {
  const navigate = useNavigate();
  const [scores, setScores] = useState(null);
  const [loading, setLoading] = useState(true);
  const [recalcing, setRecalcing] = useState(false);
  const [err, setErr] = useState(null);

  const load = async () => {
    setLoading(true); setErr(null);
    try {
      const r = await api.get('/v2/scores/me');
      setScores(r.data);
    } catch (e) {
      setErr(e.response?.data?.detail || e.message);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const recalc = async () => {
    setRecalcing(true);
    try {
      await api.post('/v2/scores/recalculate/me');
      await load();
    } catch { /* ignore */ }
    finally { setRecalcing(false); }
  };

  return (
    <div className="min-h-screen bg-white pb-16" data-testid="scoring-detail-page">
      {/* Header */}
      <div className="px-5 pt-8 pb-4 flex items-center gap-3 sticky top-0 bg-white/95 backdrop-blur-md z-20 border-b border-[var(--hairline)]">
        <button onClick={() => navigate(-1)} className="text-zinc-500 hover:text-black" data-testid="scoring-detail-back">
          <ArrowLeft size={20} />
        </button>
        <h1 className="font-brand text-[22px] font-[600] tracking-tight">Your scores</h1>
        <button onClick={recalc} disabled={recalcing || loading}
          className="ml-auto w-9 h-9 rounded-full bg-[var(--surface-2)] hover:bg-black/[0.05] flex items-center justify-center transition disabled:opacity-50"
          data-testid="scoring-detail-recalc">
          <RefreshCw size={14} className={recalcing ? 'animate-spin' : ''} />
        </button>
      </div>

      {loading && (
        <div className="px-5 pt-8 text-center text-[13px] text-[#8A8A93]" data-testid="scoring-detail-loading">
          Loading your scores…
        </div>
      )}

      {err && !loading && (
        <div className="px-5 pt-8 text-center text-[13px] text-[#B91C1C]" data-testid="scoring-detail-error">
          {err}
        </div>
      )}

      {scores && (
        <div className="px-5 pt-4 space-y-5">
          <ScoreCard
            title="Meet Score"
            subtitle="How ready you are to meet in real life"
            score={scores.meet?.score}
            band={scores.meet?.band}
            isNew={scores.meet?.is_new}
            confidence={scores.meet?.confidence}
            color="#15803D"
            icon={TrendingUp}
            testId="meet-score-card"
          />
          <BreakdownList
            breakdown={scores.meet_breakdown}
            testId="meet-breakdown"
            labels={{
              profile_readiness:  'Profile readiness',
              verification_trust: 'Verification & trust',
              engagement_comm:    'Engagement & conversations',
              date_readiness:     'Date readiness',
              reliability:        'Reliability',
            }}
          />

          <ScoreCard
            title="Reply Reliability"
            subtitle="How dependably you keep conversations going"
            score={scores.responsiveness?.score}
            band={scores.responsiveness?.band}
            isNew={scores.responsiveness?.is_new}
            confidence={scores.responsiveness?.confidence}
            color="#7C3AED"
            icon={MessageCircle}
            testId="responsiveness-score-card"
          />
          <BreakdownList
            breakdown={scores.responsiveness_breakdown}
            testId="responsiveness-breakdown"
            labels={{
              reply_rate:              'Reply rate',
              response_consistency:    'Consistency across matches',
              response_time:           'Response speed',
              follow_through:          'Conversation follow-through',
              scheduled_reliability:   'Kept calls & dates',
            }}
          />

          <div className="rounded-2xl border border-[var(--hairline)] bg-[var(--surface-2)] p-4 flex items-start gap-3" data-testid="scoring-detail-info">
            <Info size={14} className="mt-0.5 text-[#8A8A93] flex-shrink-0" />
            <p className="text-[11.5px] leading-relaxed text-[#4B4B52]">
              Scores update automatically as you complete your profile, verify, chat, call, and go on real dates. New members
              start with a neutral baseline instead of a low score — build a small history and your bars will climb. Scoring
              engine version <b>{scores.version}</b>.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}


function ScoreCard({ title, subtitle, score, band, isNew, confidence, color, icon: Icon, testId }) {
  return (
    <div data-testid={testId} className="rounded-2xl border border-[var(--hairline)] bg-white p-5 flex items-center gap-4 premium-card">
      <div className="w-16 h-16 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: `${color}12` }}>
        <p className="text-[24px] font-bold tabular-nums" style={{ color }}>{score ?? '—'}</p>
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5">
          {Icon && <Icon size={13} style={{ color }} strokeWidth={2.5} />}
          <p className="text-[10.5px] uppercase tracking-[0.14em] text-[#8A8A93] font-semibold">{title}</p>
        </div>
        <p className="text-[15px] font-semibold mt-0.5" data-testid={`${testId}-band`}>{isNew ? 'Building history' : band}</p>
        <p className="text-[11.5px] text-[#8A8A93] mt-0.5">{subtitle}</p>
        {confidence != null && !isNew && (
          <p className="text-[10.5px] text-[#8A8A93] mt-1">Confidence: {(confidence * 100).toFixed(0)}%</p>
        )}
      </div>
    </div>
  );
}


function BreakdownList({ breakdown, labels, testId }) {
  if (!breakdown) return null;
  return (
    <div className="rounded-2xl border border-[var(--hairline)] bg-white p-4 space-y-2.5" data-testid={testId}>
      {Object.entries(breakdown).map(([cat, val]) => {
        const pts = val?.points ?? 0;
        const weight = val?.weight ?? 0;
        const pct = weight > 0 ? Math.min(100, (pts / weight) * 100) : 0;
        return (
          <div key={cat} data-testid={`${testId}-row-${cat}`}>
            <div className="flex justify-between items-baseline mb-1">
              <p className="text-[12px] font-semibold text-[#0F0F10]">{labels[cat] || cat.replace(/_/g, ' ')}</p>
              <p className="text-[11px] tabular-nums text-[#4B4B52]">{Number(pts).toFixed(1)} / {weight}</p>
            </div>
            <div className="h-1.5 bg-[var(--hairline)] rounded-full overflow-hidden">
              <div className="h-full rounded-full transition-all duration-500" style={{ width: `${pct}%`, background: '#0F0F10' }} />
            </div>
          </div>
        );
      })}
    </div>
  );
}
